<script lang="ts">
  import { page } from '$app/stores';
  import HasAlertIcon from '$components/HasAlertIcon.svelte';
  import type { Race } from '$types/races';
  import { isFiltering, raceFilters } from '$utils/filters';
  import { getRaceLink, selectedRaceId } from '$utils/navigation';
  import {
    AustralianStates,
    isRaceClosed,
    raceStatusBadgeClass
  } from '$utils/race';
  import {
    currentTime,
    formatTime,
    getRelativeTimeDifference
  } from '$utils/time';
  import { activeSiteConfig, sidebarOpen } from '$utils/ui';
  import RaceTypeBadge from './RaceTypeBadge.svelte';

  $: ({ streamed, priceAlerts, eventAlerts } = $page.data);

  export let race: Race;

  let isSingleRaceFiltered = false;
  $: isSelected = $selectedRaceId === race.id;

  $: relativeTime = $activeSiteConfig.relativeEventTime;

  //   flash a light amber colour if race.startTime is within 1min
  $: closeToJump =
    (race.startTime.getTime() - $currentTime.getTime()) / 1000 < 60 &&
    race.status === 'Open';

  $: raceTimeText = () => {
    if (!relativeTime) return formatTime(race.startTime);
    else {
      const rel = getRelativeTimeDifference(race.startTime, $currentTime);
      return rel !== undefined ? `in ${rel}` : formatTime(race.startTime);
    }
  };

  function handleClick(e: MouseEvent) {
    if ($isFiltering) {
      isSingleRaceFiltered = true;
      raceFilters.toggleRaceIdFilter(race.id);
      e.preventDefault();
      return;
    }

    sidebarOpen.set(false);
    if (race.id === $selectedRaceId) return;
    if (streamed) streamed.raceData = undefined;
    selectedRaceId.set(race.id);
  }

  $: badgeClass = raceStatusBadgeClass(race, false, closeToJump);

  $: hasAlert =
    $priceAlerts?.data?.priceAlerts.some((r) => r.eventId === race.id) ||
    $eventAlerts?.data?.allEventAlerts.some(
      (alert) =>
        alert.eventId === race.id && alert?.alerts.find((a) => a.value !== '0')
    );
</script>

<li
  class="flex items-center border-b {isSelected
    ? 'hover:bg-purple-200/80'
    : 'hover:bg-sky-50'} {hasAlert && !isSelected
    ? 'bg-amber-100/60 hover:bg-amber-200/60'
    : ''}"
  class:bg-purple-100={isSelected}
  class:shadow-inner={isSelected}
  class:opacity-60={isRaceClosed(race) && !isSelected}
>
  <a
    href={getRaceLink(race)}
    on:click={handleClick}
    class="flex h-full w-full flex-shrink-0 flex-row items-center space-x-3 py-2 pl-3"
  >
    {#if $isFiltering}
      <input
        checked={isSingleRaceFiltered}
        type="checkbox"
        class="h-4 w-4 rounded border-gray-300 bg-gray-100 text-purple-700 focus:ring-2 focus:ring-purple-600"
      />
    {/if}
    <RaceTypeBadge {race} />
    <div class="min-w-0 flex-1">
      <span
        class="truncate text-sm text-gray-900"
        class:font-bold={isSelected}
        class:font-medium={!isSelected}
      >
        R{race.number}
        {race.meeting?.track}
        {#if !Object.values(AustralianStates).includes(race.meeting?.location ?? '')}
          <span class="text-xs text-gray-500">({race.meeting?.location})</span>
        {/if}
      </span>
    </div>
    <div class="flex flex-shrink-0 flex-row items-center pr-2">
      {#if hasAlert}
        <HasAlertIcon />
      {/if}
      <span
        class="items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-slate-800 shadow-sm ring-1
            {raceStatusBadgeClass(race, false, closeToJump)}"
      >
        {isRaceClosed(race) ? race.status : raceTimeText()}
      </span>
    </div>
  </a>
</li>
